import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import RecruiterLayout from '../components/RecruiterLayout';
import { Building2, Pencil, Trash2, FileText, Users as UsersIcon, CreditCard, KeyRound, Plus, X, Download } from 'lucide-react';
import { downloadLicenseInvoice, loadJobastiLogoDataUrl } from '../utils/invoicePdf';

const INDUSTRY_OPTIONS = [
  'Technology', 'Finance', 'Healthcare', 'Education', 'Manufacturing',
  'Retail', 'Real Estate', 'Consulting', 'Media', 'Automotive',
  'Energy', 'Telecommunications', 'Logistics', 'Legal', 'Other'
];

const SIZE_OPTIONS = ['1-10', '11-50', '51-200', '201-500', '501-1000', '1000+'];

const MyCompany = () => {
  const { userProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('general');
  const [company, setCompany] = useState(null);
  const [members, setMembers] = useState([]);
  const [licenseInfo, setLicenseInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [showAddMember, setShowAddMember] = useState(false);

  const orgId = userProfile?.organizationId;
  const isAdmin = userProfile?.role === 'CADM' || userProfile?.role === 'client_admin';

  useEffect(() => {
    if (orgId) {
      fetchCompany();
      fetchMembers();
      fetchLicenses();
    }
  }, [orgId]);

  const fetchCompany = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/company/${orgId}`);
      if (res.data.success) {
        setCompany(res.data.data);
        setForm(res.data.data);
      }
    } catch (err) {
      console.error('Error fetching company:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMembers = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/company/${orgId}/members`);
      if (res.data.success) setMembers(res.data.data || []);
    } catch (err) {
      console.error('Error fetching members:', err);
    }
  };

  const fetchLicenses = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/company/${orgId}/licenses`);
      if (res.data.success) setLicenseInfo(res.data.data);
    } catch (err) {
      console.error('Error fetching licenses:', err);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/company/${orgId}`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        website: form.website,
        city: form.city,
        industry: form.industry,
        company_size: form.company_size,
        description: form.description,
        street: form.street,
        postal_code: form.postal_code,
        country: form.country,
      });
      setCompany({ ...company, ...form });
      setEditing(false);
    } catch (err) {
      console.error('Error saving company:', err);
    } finally {
      setSaving(false);
    }
  };

  const updateField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const tabs = isAdmin
    ? [
        { id: 'general', label: 'General Information' },
        { id: 'licenses', label: 'Billing & Licences' },
        { id: 'members', label: 'Members' },
      ]
    : [
        { id: 'general', label: 'General Information' },
      ];

  // Aggregated invoice derived from license data — one row that rolls up base + grants.
  const computeAggregateInvoice = () => {
    if (!licenseInfo) return null;
    const baseSubtotal = licenseInfo.baseTotalAmountCents || 0;
    const grantsSubtotal = (licenseInfo.grants || []).reduce((s, g) => s + (g.totalAmountCents || 0), 0);
    const totalCents = baseSubtotal + grantsSubtotal;
    const currency = licenseInfo.currency || 'EUR';
    const issueIso = licenseInfo.paymentDate || licenseInfo.expirationDate || new Date().toISOString().slice(0, 10);
    const issueDate = (() => {
      try { return new Date(issueIso).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
      catch { return issueIso; }
    })();
    const orgIdShort = (company?.id || '').slice(0, 6).toUpperCase() || 'XXXXXX';
    const year = (() => { try { return new Date(issueIso).getFullYear(); } catch { return new Date().getFullYear(); } })();
    const number = `INV-JBS-${year}-${orgIdShort}`;
    const amount = totalCents === 0
      ? '—'
      : (() => { try { return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(totalCents / 100); } catch { return `${(totalCents / 100).toFixed(2)} ${currency}`; } })();
    return { number, date: issueDate, amount };
  };

  const handleDownloadInvoice = async () => {
    if (!company || !licenseInfo) return;
    const logo = await loadJobastiLogoDataUrl();
    const inv = computeAggregateInvoice();
    downloadLicenseInvoice({ company, licenseInfo, logoDataUrl: logo, invoiceNumber: inv?.number });
  };

  const handleCancelInvitation = async (invitationId, email) => {
    if (!window.confirm(`Cancel the pending invitation for ${email}?`)) return;
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/company/${orgId}/invitations/${invitationId}`);
      await fetchMembers();
      await fetchLicenses();
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to cancel invitation');
    }
  };

  const handlePasswordReset = async (email, name) => {
    if (!window.confirm(`Send a password reset email to ${name} (${email})?`)) return;
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`, { email });
      alert(`Password reset link sent to ${email}`);
    } catch {
      alert('Failed to send password reset email');
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'client_admin': return 'Administrator';
      case 'client_recruiter': return 'Recruiter';
      default: return role || '-';
    }
  };

  const getRoleBadgeStyle = (role) => {
    switch (role) {
      case 'client_admin': return 'bg-blue-100 text-blue-700';
      case 'client_recruiter': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  if (loading) {
    return (
      <RecruiterLayout>
        <div className="max-w-4xl mx-auto px-4 py-12 text-center text-gray-500">Loading...</div>
      </RecruiterLayout>
    );
  }

  return (
    <RecruiterLayout title="Company">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="my-company-page">
        {/* Company Header */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-center">
                <Building2 className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-900" data-testid="company-name">{company?.name || 'Company'}</h1>
                <p className="text-sm text-gray-500">ID {company?.id?.slice(0, 8)}</p>
              </div>
            </div>
            {isAdmin && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setEditing(!editing)}
                  className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  data-testid="edit-company-btn"
                >
                  <Pencil className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6" data-testid="company-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200">

          {/* === GENERAL INFORMATION TAB === */}
          {activeTab === 'general' && (
            <div className="p-6" data-testid="tab-content-general">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">General Information</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Company Name" value={form.name} onChange={v => updateField('name', v)} editing={editing} />
                <Field label="City" value={form.city} onChange={v => updateField('city', v)} editing={editing} />
                <Field label="Street" value={form.street} onChange={v => updateField('street', v)} editing={editing} />
                <Field label="Post Code" value={form.postal_code} onChange={v => updateField('postal_code', v)} editing={editing} />
                <Field label="Phone" value={form.phone} onChange={v => updateField('phone', v)} editing={editing} />
                <SelectField label="Industry" value={form.industry} options={INDUSTRY_OPTIONS} onChange={v => updateField('industry', v)} editing={editing} />
                <SelectField label="Company Size" value={form.company_size} options={SIZE_OPTIONS} onChange={v => updateField('company_size', v)} editing={editing} />
                <Field label="Website" value={form.website} onChange={v => updateField('website', v)} editing={editing} />
                <Field label="E-mail" value={form.email} onChange={v => updateField('email', v)} editing={editing} />
                <Field label="Country" value={form.country} onChange={v => updateField('country', v)} editing={editing} />
              </div>

              {/* About / Description */}
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">About the Company</label>
                {editing ? (
                  <textarea
                    value={form.description || ''}
                    onChange={e => updateField('description', e.target.value)}
                    rows={5}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
                    placeholder="Write about your company, culture, mission..."
                    data-testid="company-description-input"
                  />
                ) : (
                  <p className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3 min-h-[80px] whitespace-pre-wrap">
                    {form.description || 'No description added yet.'}
                  </p>
                )}
              </div>

              {editing && (
                <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
                  <button
                    onClick={() => { setForm(company); setEditing(false); }}
                    className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    data-testid="save-company-btn"
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* === LICENSES TAB === */}
          {activeTab === 'licenses' && isAdmin && (
            <div className="p-6" data-testid="tab-content-licenses">
              {/* Invoices */}
              <div className="mb-8">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Invoices</h2>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="invoices-table">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-medium text-gray-700">Number</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700">Date</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700">Amount</th>
                        <th className="text-right py-3 px-4 font-medium text-gray-700">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const inv = computeAggregateInvoice();
                        if (!inv) {
                          return (
                            <tr>
                              <td colSpan={4} className="py-6 px-4 text-center text-sm text-gray-500" data-testid="invoices-empty">No invoices yet — license details will appear here once configured.</td>
                            </tr>
                          );
                        }
                        return (
                          <tr className="border-b border-gray-100 last:border-0" data-testid="invoice-row">
                            <td className="py-3 px-4 text-gray-800" data-testid="invoice-number">{inv.number}</td>
                            <td className="py-3 px-4 text-gray-600" data-testid="invoice-date">{inv.date}</td>
                            <td className="py-3 px-4 text-gray-800 font-medium" data-testid="invoice-amount">{inv.amount}</td>
                            <td className="py-3 px-4 text-right">
                              <button
                                onClick={handleDownloadInvoice}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-gray-700 bg-white border border-gray-200 rounded-lg hover:border-gray-300 hover:bg-gray-50 transition-colors"
                                title="Download invoice PDF"
                                data-testid="invoice-download-btn"
                              >
                                <Download className="w-3.5 h-3.5" /> Download PDF
                              </button>
                            </td>
                          </tr>
                        );
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Licenses */}
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Billing &amp; Licences</h2>
                {licenseInfo ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="licenses-table">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-3 px-4 font-medium text-gray-700">Number of Licenses</th>
                          <th className="text-left py-3 px-4 font-medium text-gray-700">Active Licenses</th>
                          <th className="text-left py-3 px-4 font-medium text-gray-700">Amount</th>
                          <th className="text-left py-3 px-4 font-medium text-gray-700">Expiration Date</th>
                          <th className="text-left py-3 px-4 font-medium text-gray-700">Payment Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* Base row */}
                        <tr className="border-b border-gray-100">
                          <td className="py-3 px-4 text-gray-800 font-semibold" data-testid="license-total-value">{licenseInfo.base}</td>
                          <td className="py-3 px-4 text-gray-800" data-testid="license-used-value">{licenseInfo.used}</td>
                          <td className="py-3 px-4 text-gray-700" data-testid="license-amount-value">
                            {licenseInfo.baseTotalAmountCents > 0 ? (
                              <span>
                                {formatLicenseMoney(licenseInfo.baseTotalAmountCents, licenseInfo.currency)}
                                <span className="text-[11px] text-gray-500 ml-1">total</span>
                              </span>
                            ) : '—'}
                          </td>
                          <td className="py-3 px-4 text-gray-600" data-testid="license-expiration-value">{formatLicenseDate(licenseInfo.expirationDate)}</td>
                          <td className="py-3 px-4 text-gray-600" data-testid="license-payment-value">{formatLicenseDate(licenseInfo.paymentDate)}</td>
                        </tr>
                        {/* Grant rows */}
                        {(licenseInfo.grants || []).map((g) => (
                          <tr key={g.id} className="border-b border-gray-100 last:border-0" data-testid={`license-grant-row-${g.id}`}>
                            <td className="py-3 px-4 text-gray-800 font-semibold">{g.seats}</td>
                            <td className="py-3 px-4 text-gray-400">—</td>
                            <td className="py-3 px-4 text-gray-700">
                              {g.totalAmountCents > 0 ? (
                                <span>
                                  {formatLicenseMoney(g.totalAmountCents, licenseInfo.currency)}
                                  <span className="text-[11px] text-gray-500 ml-1">total</span>
                                </span>
                              ) : '—'}
                            </td>
                            <td className="py-3 px-4 text-gray-600">{formatLicenseDate(g.expirationDate)}</td>
                            <td className="py-3 px-4 text-gray-600">{formatLicenseDate(g.paymentDate)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Loading…</p>
                )}
              </div>
            </div>
          )}

          {/* === MEMBERS TAB === */}
          {activeTab === 'members' && isAdmin && (
            <div className="p-6" data-testid="tab-content-members">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Members</h2>
                  {licenseInfo && (
                    <p className="text-xs text-gray-500 mt-1">
                      <span className="font-semibold text-gray-700">{licenseInfo.used}</span> of <span className="font-semibold text-gray-700">{licenseInfo.total}</span> licenses used
                      {' · '}
                      <span className={licenseInfo.available > 0 ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                        {licenseInfo.available} available
                      </span>
                    </p>
                  )}
                </div>
                <button
                  onClick={() => setShowAddMember(true)}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg"
                  data-testid="add-member-btn"
                >
                  <Plus className="w-4 h-4" /> Add member
                </button>
              </div>

              {/* Members Table */}
              <div className="overflow-x-auto mb-8">
                <table className="w-full text-sm" data-testid="members-table">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 font-medium text-gray-700">Name</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">Role</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">E-mail</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">Status</th>
                      <th className="text-right py-3 px-4 font-medium text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map((m) => (
                      <tr key={m.id} className="border-b border-gray-100 last:border-0" data-testid={`member-row-${m.id}`}>
                        <td className="py-3 px-4">
                          <span className="font-medium text-gray-900">
                            {(m.first_name || m.last_name) ? `${m.first_name || ''} ${m.last_name || ''}`.trim() : <span className="text-gray-400 italic">Invited user</span>}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="inline-flex items-center gap-1.5">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleBadgeStyle(m.role)}`}>
                              {getRoleLabel(m.role)}
                            </span>
                            {m.license_exempt && (
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-600" title="Does not consume a license">
                                Exempt
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-gray-600">{m.email}</td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            m.is_pending ? 'bg-amber-100 text-amber-700'
                            : m.status === 'active' ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-600'
                          }`} data-testid={`member-status-${m.id}`}>
                            {m.is_pending ? 'pending' : (m.status || 'active')}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            {m.is_pending ? (
                              <button
                                onClick={() => handleCancelInvitation(m.invitation_id, m.email)}
                                className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                                title="Cancel invitation"
                                data-testid={`cancel-invite-${m.invitation_id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            ) : (
                              <>
                                <button
                                  onClick={() => handlePasswordReset(m.email, `${m.first_name} ${m.last_name}`)}
                                  className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded transition-colors"
                                  title="Send password reset"
                                  data-testid={`reset-pwd-${m.id}`}
                                >
                                  <KeyRound className="w-4 h-4" />
                                </button>
                                <button className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors">
                                  <Pencil className="w-4 h-4" />
                                </button>
                                <button className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors">
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {members.length === 0 && (
                      <tr>
                        <td colSpan={5} className="py-8 text-center text-gray-500">No members found</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Team overview */}
              <div>
                <h3 className="text-base font-semibold text-gray-900 mb-4">Team Overview</h3>
                <div className="space-y-6">
                  {['Administrator', 'Recruiter'].map(roleLabel => {
                    const roleKey = roleLabel === 'Administrator' ? 'client_admin' : 'client_recruiter';
                    const roleMembers = members.filter(m => m.role === roleKey);
                    if (roleMembers.length === 0) return null;
                    return (
                      <div key={roleLabel} className="flex items-start gap-6" data-testid={`team-${roleLabel.toLowerCase()}`}>
                        <div className="w-28 flex-shrink-0 pt-2">
                          <p className="text-sm font-medium text-gray-500">{roleLabel}</p>
                        </div>
                        <div className="flex flex-wrap gap-3">
                          {roleMembers.map(m => (
                            <div key={m.id} className="bg-gray-800 text-white rounded-lg p-3 w-44">
                              <div className="text-xs font-bold bg-blue-600 rounded px-2 py-0.5 inline-block mb-1.5">
                                {roleLabel}
                              </div>
                              <p className="text-sm font-medium">{m.first_name} {m.last_name}</p>
                              <p className="text-xs text-gray-400 truncate">{m.email}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* === MEMBERS TAB END === */}

        </div>
      </div>

      {showAddMember && (
        <AddMemberModal
          orgId={orgId}
          actorUserId={userProfile?.id}
          actorName={[userProfile?.firstName, userProfile?.lastName].filter(Boolean).join(' ') || userProfile?.email}
          availableSeats={licenseInfo?.available ?? 0}
          onClose={() => setShowAddMember(false)}
          onSent={() => {
            setShowAddMember(false);
            fetchMembers();
            fetchLicenses();
          }}
        />
      )}
    </RecruiterLayout>
  );
};

// Reusable text field component
const Field = ({ label, value, onChange, editing }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    {editing ? (
      <input
        type="text"
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      />
    ) : (
      <p className="text-sm text-gray-800 bg-gray-50 rounded-lg px-3 py-2 min-h-[38px]">{value || '-'}</p>
    )}
  </div>
);

// Reusable select field component
const SelectField = ({ label, value, options, onChange, editing }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    {editing ? (
      <select
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
      >
        <option value="">Select...</option>
        {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
      </select>
    ) : (
      <p className="text-sm text-gray-800 bg-gray-50 rounded-lg px-3 py-2 min-h-[38px]">{value || '-'}</p>
    )}
  </div>
);

export default MyCompany;

const formatLicenseDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return iso; }
};

const formatLicenseMoney = (cents, currency = 'EUR') => {
  if (!cents || cents === 0) return '—';
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
};

const AddMemberModal = ({ orgId, actorUserId, actorName, availableSeats, onClose, onSent }) => {
  const [form, setForm] = useState({ email: '', firstName: '', lastName: '', role: 'client_recruiter', licenseExempt: false });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null); // { emailSent, emailError, registrationUrl, email }
  const [copied, setCopied] = useState(false);

  const backendBase = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

  const send = async () => {
    setError('');
    if (!form.email) { setError('Email is required'); return; }
    if (!form.licenseExempt && availableSeats <= 0) {
      setError('No licenses available. Tick "Doesn\'t count toward licenses" or ask JobastiIQ to raise the cap.');
      return;
    }
    setSending(true);
    try {
      const r = await axios.post(`${backendBase}/api/company/${orgId}/invite`, { ...form, actorUserId, actorName });
      setResult(r.data?.data || { emailSent: false, registrationUrl: '', email: form.email });
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to send invitation');
    } finally {
      setSending(false);
    }
  };

  const copyLink = async () => {
    if (!result?.registrationUrl) return;
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(result.registrationUrl);
        return;
      }
    } catch { /* fall through to legacy */ }
    // Legacy fallback for insecure contexts / headless browsers.
    try {
      const ta = document.createElement('textarea');
      ta.value = result.registrationUrl;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    } catch { /* ignore */ }
  };

  const closeAndRefresh = () => {
    if (result) onSent();
    else onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={closeAndRefresh} data-testid="add-member-modal-overlay">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="add-member-modal">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900">{result ? 'Invitation sent' : 'Add a new member'}</h2>
            <p className="text-xs text-gray-500 mt-0.5">{result ? 'You can copy the registration link below as a backup.' : 'They\u2019ll receive an email with a registration link.'}</p>
          </div>
          <button onClick={closeAndRefresh} className="p-2 hover:bg-gray-100 rounded-lg" aria-label="Close" data-testid="add-member-modal-close">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        {!result ? (
          <>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">First name</label>
                  <input type="text" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} className="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500" data-testid="add-member-firstname" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">Last name</label>
                  <input type="text" value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} className="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500" data-testid="add-member-lastname" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Email <span className="text-red-500">*</span></label>
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500" data-testid="add-member-email" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Role</label>
                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500" data-testid="add-member-role">
                  <option value="client_recruiter">Recruiter</option>
                  <option value="client_admin">Administrator</option>
                </select>
              </div>
              <label className="flex items-start gap-2.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={form.licenseExempt}
                  onChange={(e) => setForm({ ...form, licenseExempt: e.target.checked })}
                  className="mt-0.5 w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                  data-testid="add-member-license-exempt"
                />
                <span>
                  <span className="block text-xs font-semibold text-gray-800">Doesn&apos;t count toward licenses</span>
                  <span className="block text-[11px] text-gray-500 leading-snug mt-0.5">Use for observers, contractors, or members who shouldn&apos;t consume a paid license.</span>
                </span>
              </label>
              {!form.licenseExempt && (
                <p className="text-[11px] text-gray-500">
                  {availableSeats > 0 ? `${availableSeats} license${availableSeats === 1 ? '' : 's'} available.` : 'No licenses available — tick the exempt option above or contact JobastiIQ to add more.'}
                </p>
              )}
              {error && <p className="text-xs text-red-600" data-testid="add-member-error">{error}</p>}
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3">
              <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900" data-testid="add-member-cancel">Cancel</button>
              <button onClick={send} disabled={sending} className="px-5 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 rounded-lg" data-testid="add-member-send">
                {sending ? 'Sending…' : 'Send invitation'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="p-6 space-y-4" data-testid="add-member-success">
              <div className={`p-3 rounded-lg text-sm ${result.emailSent ? 'bg-green-50 text-green-800 border border-green-100' : 'bg-amber-50 text-amber-900 border border-amber-100'}`}>
                {result.emailSent
                  ? <span><span className="font-semibold">Email sent</span> to {result.email}. They&apos;ll see this in their inbox shortly.</span>
                  : <span><span className="font-semibold">Email delivery failed</span> — the invitation is still active. Share the link below with {result.email} directly.{result.emailError ? <span className="block mt-1 text-[11px] opacity-80">{result.emailError}</span> : null}</span>}
              </div>
              {result.registrationUrl && (
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">Registration link</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text" readOnly value={result.registrationUrl}
                      onClick={(e) => e.target.select()}
                      className="flex-1 text-xs border border-gray-300 rounded px-3 py-2 bg-gray-50 font-mono"
                      data-testid="add-member-registration-url"
                    />
                    <button
                      onClick={copyLink}
                      className="px-3 py-2 text-xs font-semibold text-gray-700 bg-white border border-gray-200 rounded hover:bg-gray-50"
                      data-testid="add-member-copy-link"
                    >
                      {copied ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3">
              <button onClick={() => { setResult(null); setForm({ email: '', firstName: '', lastName: '', role: 'client_recruiter', licenseExempt: false }); }} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900" data-testid="add-member-invite-another">Invite another</button>
              <button onClick={onSent} className="px-5 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg" data-testid="add-member-done">Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
